import {
  AttendanceBreakSetting as BreakSetting,
  AttendanceBreakDurationRule as DurationRule,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'

import validateBreakDurationRules, {
  ErrorTypes,
  ValidationError,
} from '../validateShiftBreakDurationRules'

export const MAX_BREAKS_LENGTH_RULES = 3

class BreakSettings {
  // Temporal method that returns default values in the old format.
  // Migrated here to remove the old BreakSettings Mobx Collection.
  // TODO: Change the default values to the new format after removing the Model.
  static defaultValues = {
    manual_breaks: false,
    automatic_breaks: false,
    automatic_break_rules_attributes: [],
    duration_rules_attributes: [],
    rest_time: false,
    send_notification: '',
  }

  static durationRules(
    breakSetting: Pick<BreakSetting, 'manualBreaks' | 'attendanceBreakDurationRules'> & {
      attendanceBreakDurationRules: Pick<DurationRule, 'id' | 'minimalDuration' | 'periodLength'>[]
    }
  ): DurationRule[] {
    if (!breakSetting.manualBreaks) return []

    return breakSetting.attendanceBreakDurationRules
      .slice()
      .sort(
        (firstRule: DurationRule, secondRule: DurationRule) =>
          secondRule.periodLength - firstRule.periodLength
      )
  }

  static currentDurationRule(
    breakSetting: Pick<BreakSetting, 'manualBreaks' | 'attendanceBreakDurationRules'> & {
      attendanceBreakDurationRules: Pick<DurationRule, 'id' | 'minimalDuration' | 'periodLength'>[]
    },
    workedMinutes: number
  ): DurationRule | null {
    if (!breakSetting.manualBreaks) return null

    const durationRules = this.durationRules(breakSetting)
    return durationRules.find((rule) => workedMinutes >= (rule.periodLength / 100) * 60) ?? null
  }

  static breakTimeRequired(
    breakSetting: Pick<BreakSetting, 'manualBreaks' | 'attendanceBreakDurationRules'> & {
      attendanceBreakDurationRules: Pick<DurationRule, 'id' | 'minimalDuration' | 'periodLength'>[]
    },
    workedMinutes: number
  ): number {
    const rule = this.currentDurationRule(breakSetting, workedMinutes)

    return rule?.minimalDuration ?? 0
  }

  static lessMinBreak(
    breakSetting: Pick<BreakSetting, 'manualBreaks' | 'restTime' | 'baseMinimalDuration'>,
    duration: number
  ): boolean {
    if (!breakSetting.manualBreaks) return false
    if (!breakSetting.restTime) return false
    if (!breakSetting.baseMinimalDuration) return false

    return breakSetting.baseMinimalDuration > duration
  }
  static hasManualBreaksWithRules(
    breakSetting: Pick<
      BreakSetting,
      'manualBreaks' | 'attendanceBreakDurationRules' | 'restTime'
    > | null
  ): boolean {
    if (!breakSetting) return false
    if (!breakSetting.manualBreaks) return false
    if (!breakSetting.restTime) return false

    return breakSetting.attendanceBreakDurationRules.length > 0
  }

  static hasAutofixMissingBreaks(
    breakSetting: Pick<BreakSetting, 'manualBreaks' | 'breakViolationsHandling' | 'restTime'> | null
  ): boolean {
    if (!breakSetting) return false
    if (!breakSetting.manualBreaks) return false
    if (!breakSetting.restTime) return false

    return breakSetting.breakViolationsHandling == 'autofix'
  }

  static missingBreaksAlerts = (
    breakSetting: Pick<
      BreakSetting,
      'manualBreaks' | 'restTime' | 'attendanceBreakDurationRules' | 'baseMinimalDuration'
    > | null,
    todayShifts: TimesheetPageShiftFragment[]
  ): ValidationError[] => {
    if (breakSetting) {
      const errors = validateBreakDurationRules(todayShifts, breakSetting)
      return (
        errors
          .filter((item) => item.error === ErrorTypes.BREAK_MINIMUM)
          .slice(0, 1)
          .concat(
            errors
              .filter((item) => item.error === ErrorTypes.REST_TIME)
              .sort((a, b) => (b.periodLength ?? 0) - (a.periodLength ?? 0))
              .slice(0, 1)
          ) ?? []
      )
    }

    return []
  }
}

export default BreakSettings
